import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, Marker } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { useMapContext } from '../context/MapContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import EditableMap from './EditableMap';
import 'leaflet-draw/dist/leaflet.draw.css';

// Helper component to access the map instance
const MapController = ({ geojsonData, onFeatureClick }) => {
  const map = useMap();
  const { mapRef } = useMapContext();
  
  // Store map reference
  useEffect(() => {
    if (map) {
      mapRef.current = map;
    }
  }, [map, mapRef]);
  
  // Fit bounds when data changes
  useEffect(() => {
    if (map && geojsonData && geojsonData.features && geojsonData.features.length > 0) {
      try {
        // Create a temporary GeoJSON layer to get bounds
        const tempLayer = L.geoJSON(geojsonData);
        const bounds = tempLayer.getBounds();
        
        if (bounds.isValid()) {
          map.fitBounds(bounds);
        }
      } catch (error) {
        console.error('Error fitting bounds:', error);
      }
    }
  }, [map, geojsonData]);
  
  return null;
};

const MapEditor = ({ setLoading }) => {
  const { t } = useTranslation();
  const { 
    geojsonData, 
    selectFeature, 
    selectedFeature,
    isEditing,
    finishEditing,
    getColorForDUN
  } = useMapContext();
  
  const [key, setKey] = useState(0); // Key to force re-render
  
  // Event handler for feature click
  const onFeatureClick = (feature, layer) => {
    // Bring the clicked layer to front to ensure it's visible above other polygons
    if (layer) {
      layer.bringToFront();
    }
    
    // Select the feature
    selectFeature(feature, layer._leaflet_id);
  };
  
  // Style function for GeoJSON features
  const getFeatureStyle = (feature) => {
    const isSelected = selectedFeature && selectedFeature.feature === feature;
    
    return {
      fillColor: getColorForDUN(feature.properties.DUN_CODE),
      weight: isSelected ? 4 : 2,
      opacity: 1,
      color: isSelected ? '#666' : 'white',
      dashArray: isSelected ? '' : '3',
      fillOpacity: isSelected ? 0.8 : 0.7
    };
  };
  
  // Handle each feature in the GeoJSON
  const onEachFeature = (feature, layer) => {
    // Create popup content with standardized field names and translations
    const properties = feature.properties;
    
    // Translate field labels and default values
    const unnamed = t('mapEditor.unnamed', 'Unnamed');
    const noCode = t('mapEditor.noCode', 'No Code');
    const unknown = t('mapEditor.unknown', 'Unknown');
    
    // Fields with translations
    const parliamentLabel = t('propertyEditor.labels.PARLIAMENT', 'Parliament');
    const stateLabel = t('propertyEditor.labels.STATE', 'State');
    const coalitionLabel = t('propertyEditor.labels.COALITION', 'Coalition');
    const partyLabel = t('propertyEditor.labels.POLITICAL_PARTY', 'Party');
    const representativeLabel = t('mapEditor.representative', 'Representative');
    
    const popupContent = `
      <div class="info-box">
        <h3>${properties.DUN_NAME || unnamed} (${properties.DUN_CODE || noCode})</h3>
        <p><strong>${parliamentLabel}:</strong> ${properties.PARLIAMENT || unknown} (${properties.PARLIAMENT_CODE || noCode})</p>
        <p><strong>${stateLabel}:</strong> ${properties.STATE || unknown}</p>
        <p><strong>${coalitionLabel}:</strong> ${properties.COALITION || unknown}</p>
        <p><strong>${partyLabel}:</strong> ${properties.POLITICAL_PARTY || unknown}</p>
        <p><strong>${representativeLabel}:</strong> ${properties.ADUN_NICKNAME || properties.ADUN_FULLNAME || unknown}</p>
      </div>
    `;
    
    // Bind popup to layer
    layer.bindPopup(popupContent);
    
    // Add click handler
    layer.on('click', () => {
      // Bring layer to front when clicked
      layer.bringToFront();
      onFeatureClick(feature, layer);
    });
    
    // Also bring layer to front when mouse enters
    layer.on('mouseover', () => {
      layer.bringToFront();
    });
  };
  
  // Update key to force GeoJSON refresh when data changes
  useEffect(() => {
    setKey(prevKey => prevKey + 1);
    setLoading(false);
  }, [geojsonData, setLoading]);
  
  // Force update when selected feature changes
  useEffect(() => {
    setKey(prevKey => prevKey + 1);
  }, [selectedFeature]);
  
  return (
    <div className="map-container">
      <MapContainer 
        center={[4.8, 100.7]} 
        zoom={7} 
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
      >
        <TileLayer
          attribution=''
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {geojsonData && !isEditing && (
          <>
            <GeoJSON 
              key={key}
              data={geojsonData} 
              style={getFeatureStyle}
              onEachFeature={onEachFeature}
            />
            
            {/* Add labels for DUN codes at the center of each area */}
            {geojsonData.features.map((feature, index) => {
              // Calculate center of the feature
              const layer = L.geoJSON(feature);
              const center = layer.getBounds().getCenter();
              
              return (
                <Marker 
                  key={`label-${index}`}
                  position={[center.lat, center.lng]}
                  icon={L.divIcon({
                    className: 'dun-label-icon',
                    html: `<div class="dun-label">${feature.properties.DUN_CODE || ''}</div>`,
                    iconSize: [40, 20],
                    iconAnchor: [20, 10]
                  })}
                />
              );
            })}
          </>
        )}
        
        {/* Editable version of the map */}
        {selectedFeature && (
          <EditableMap 
            selectedFeature={selectedFeature}
            isEditing={isEditing}
            onEditComplete={(geometry) => {
              if (geometry) {
                finishEditing(geometry);
              }
            }}
          />
        )}
        
        <MapController 
          geojsonData={geojsonData}
          onFeatureClick={onFeatureClick}
        />
      </MapContainer>
      
      {/* Simple info message */}
      <div className="map-notice" style={{
        position: 'absolute',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(255,255,255,0.8)',
        padding: '8px 15px',
        borderRadius: '4px',
        zIndex: 1000,
        display: !geojsonData ? 'block' : 'none'
      }}>
        {t('mapEditor.loadMessage', 'Load a GeoJSON file to display electoral boundaries')}
      </div>
    </div>
  );
};

export default MapEditor;
